import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleQuestion,
  faPaperPlane,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  streamPlatformHelpMessage,
  resetPlatformHelpThread,
} from "services/platformHelpBot";
import css from "./PlatformHelpBot.module.scss";

function makeThreadId() {
  return `ph_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function staffUserDetails(user) {
  const type = user?.type;
  const isAdmin = type === 3 || type === -1;
  const userType = isAdmin ? "admin" : "teacher";
  return {
    userId: String(user.id),
    userRole: userType,
    firstName: user.firstName || "User",
    lastName: user.lastName || "User",
    email: user.email || "user@example.com",
    userType,
  };
}

const SUGGESTIONS = [
  "How do I switch organization or campus?",
  "Where do I find classes and rosters?",
  "How do I create or schedule a session?",
  "What should I check if learners can't see my course?",
];

const STELLA_SESSION_KEY = "ph_stella_intro_shown";
const FAB_CORNER_KEY = "ph_fab_corner";
const PANEL_CORNER_KEY = "ph_panel_corner";
const DRAG_THRESHOLD = 6;
const FAB_SIZE = 56;
const GAP = 20;

const STELLA_LINE1 = "Hi, I'm Stella.";
const STELLA_LINE2 =
  "I can help you find your way around the platform—menus, publishing, classes, and admin tools. Tap the button anytime to chat.";

function readCorner(key, fallback) {
  try {
    const v = sessionStorage.getItem(key);
    if (v === "br" || v === "bl" || v === "tr" || v === "tl") return v;
  } catch (_e) {
    /* ignore */
  }
  return fallback;
}

function persistCorner(key, corner) {
  try {
    sessionStorage.setItem(key, corner);
  } catch (_e) {
    /* ignore */
  }
}

/** Nearest corner by distance to inset anchor points (matches FAB padding). */
function nearestCorner(clientX, clientY) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const pad = GAP + FAB_SIZE / 2;
  const anchors = {
    br: { x: w - pad, y: h - pad },
    bl: { x: pad, y: h - pad },
    tr: { x: w - pad, y: pad },
    tl: { x: pad, y: pad },
  };
  let best = "br";
  let bestD = Infinity;
  Object.keys(anchors).forEach((k) => {
    const c = anchors[k];
    const d = (clientX - c.x) ** 2 + (clientY - c.y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  });
  return best;
}

function clampFabPosition(left, top, rootW, rootH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxL = Math.max(0, vw - rootW);
  const maxT = Math.max(0, vh - rootH);
  return {
    left: Math.min(Math.max(0, left), maxL),
    top: Math.min(Math.max(0, top), maxT),
  };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fn = () => setReduced(!!mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return reduced;
}

export default function PlatformHelpBot({ ctx_user, location }) {
  const history = useHistory();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(makeThreadId);
  const [introStep, setIntroStep] = useState(0);
  const [fabCorner, setFabCorner] = useState(() => readCorner(FAB_CORNER_KEY, "br"));
  const [panelCorner, setPanelCorner] = useState(() =>
    readCorner(PANEL_CORNER_KEY, "br")
  );
  const [fabDragPos, setFabDragPos] = useState(null);
  const [fabDragging, setFabDragging] = useState(false);
  const [panelDragPos, setPanelDragPos] = useState(null);
  const [panelDragging, setPanelDragging] = useState(false);
  const [isShy, setIsShy] = useState(false);
  const [fabHovered, setFabHovered] = useState(false);

  const messagesEndRef = useRef(null);
  const introTimersRef = useRef([]);
  const fabRootRef = useRef(null);
  const panelRef = useRef(null);
  const fabDragState = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    moved: false,
  });
  const panelDragState = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    moved: false,
  });
  const shyTimerRef = useRef(null);

  const clearIntroTimers = useCallback(() => {
    introTimersRef.current.forEach((id) => clearTimeout(id));
    introTimersRef.current = [];
  }, []);

  const dismissIntro = useCallback(() => {
    clearIntroTimers();
    setIntroStep(0);
    try {
      sessionStorage.setItem(STELLA_SESSION_KEY, "1");
    } catch (_e) {
      /* ignore */
    }
  }, [clearIntroTimers]);

  useEffect(() => {
    if (!ctx_user?.user) return;
    try {
      if (sessionStorage.getItem(STELLA_SESSION_KEY)) return;
    } catch (_e) {
      return;
    }

    const schedule = (fn, ms) => {
      const id = setTimeout(fn, ms);
      introTimersRef.current.push(id);
    };

    schedule(() => {
      try {
        sessionStorage.setItem(STELLA_SESSION_KEY, "1");
      } catch (_e) {
        /* ignore */
      }
      setIntroStep(1);
    }, 1200);
    schedule(() => setIntroStep(2), 1650);
    schedule(() => setIntroStep(0), 1200 + 450 + 9000);

    return () => clearIntroTimers();
  }, [ctx_user?.user, clearIntroTimers]);

  useEffect(() => {
    if (!open) return;
    clearIntroTimers();
    setIntroStep(0);
    try {
      sessionStorage.setItem(STELLA_SESSION_KEY, "1");
    } catch (_e) {
      /* ignore */
    }
  }, [open, clearIntroTimers]);

  /** Shy FAB on scroll (debounced clear). */
  useEffect(() => {
    if (prefersReducedMotion || open || introStep > 0) {
      setIsShy(false);
      return undefined;
    }

    const bumpShy = () => {
      setIsShy(true);
      if (shyTimerRef.current) clearTimeout(shyTimerRef.current);
      shyTimerRef.current = setTimeout(() => {
        shyTimerRef.current = null;
        setIsShy(false);
      }, 800);
    };

    window.addEventListener("scroll", bumpShy, { passive: true });
    return () => {
      window.removeEventListener("scroll", bumpShy);
      if (shyTimerRef.current) clearTimeout(shyTimerRef.current);
    };
  }, [prefersReducedMotion, open, introStep]);

  useEffect(() => {
    if (fabHovered || open || introStep > 0) setIsShy(false);
  }, [fabHovered, open, introStep]);

  useEffect(() => {
    const onResize = () => {
      setFabDragPos(null);
      setPanelDragPos(null);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, open]);

  const handleNewChat = async () => {
    const oldId = threadId;
    try {
      await resetPlatformHelpThread(oldId);
    } catch (e) {
      console.warn("Platform Help reset thread:", e);
    }
    setThreadId(makeThreadId());
    setMessages([]);
    setInput("");
    setLoading(false);
  };

  const sendText = useCallback(
    async (text) => {
      const trimmed = String(text || "").trim();
      if (!trimmed || loading || !ctx_user?.user) return;

      const user = ctx_user.user;
      const userDetails = staffUserDetails(user);
      const pageContext = {
        pathname: location?.pathname || "",
        pageName: document?.title || "",
      };

      const userMessage = {
        id: `u_${Date.now()}`,
        type: "user",
        content: trimmed,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      const assistantId = `a_${Date.now()}`;
      const assistantPlaceholder = {
        id: assistantId,
        type: "assistant",
        content: "",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isProcessing: true,
      };

      setMessages((m) => [...m, userMessage, assistantPlaceholder]);
      setInput("");
      setLoading(true);

      try {
        await streamPlatformHelpMessage(
          trimmed,
          threadId,
          pageContext,
          userDetails,
          (_chunk, accumulated) => {
            setMessages((prev) => {
              const next = [...prev];
              const idx = next.findIndex((msg) => msg.id === assistantId);
              if (idx >= 0) {
                next[idx] = {
                  ...next[idx],
                  content: accumulated,
                  isProcessing: false,
                };
              }
              return next;
            });
          },
          (final) => {
            setMessages((prev) => {
              const next = [...prev];
              const idx = next.findIndex((msg) => msg.id === assistantId);
              if (idx >= 0) {
                next[idx] = {
                  ...next[idx],
                  content: final,
                  isProcessing: false,
                };
              }
              return next;
            });
            setLoading(false);
          },
          (err) => {
            setMessages((prev) => {
              const next = [...prev];
              const idx = next.findIndex((msg) => msg.id === assistantId);
              if (idx >= 0) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (err && err.message) ||
                    "Something went wrong. Check that Azure OpenAI is configured for Platform Help.",
                  isProcessing: false,
                  hasError: true,
                };
              }
              return next;
            });
            setLoading(false);
          },
          {
            onLinks: (urls) => {
              const safe =
                Array.isArray(urls) &&
                urls
                  .filter((u) => u && typeof u.href === "string" && u.href.startsWith("/"))
                  .map((u) => ({
                    href: u.href,
                    label:
                      typeof u.label === "string" && u.label.trim()
                        ? u.label.trim()
                        : u.href,
                  }));
              if (!safe || !safe.length) return;
              setMessages((prev) => {
                const next = [...prev];
                const idx = next.findIndex((msg) => msg.id === assistantId);
                if (idx >= 0) {
                  next[idx] = { ...next[idx], links: safe };
                }
                return next;
              });
            },
          }
        );
      } catch (err) {
        setMessages((prev) => {
          const next = [...prev];
          const idx = next.findIndex((msg) => msg.id === assistantId);
          if (idx >= 0) {
            next[idx] = {
              ...next[idx],
              content:
                (err && err.message) ||
                "Request failed. Try again or contact support.",
              isProcessing: false,
              hasError: true,
            };
          }
          return next;
        });
        setLoading(false);
      }
    },
    [ctx_user, location, loading, threadId]
  );

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText(input);
    }
  };

  const fabPointerDown = (e) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    const root = fabRootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    fabDragState.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_err) {
      /* ignore */
    }
  };

  const fabPointerMove = (e) => {
    const s = fabDragState.current;
    if (s.pointerId !== e.pointerId) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && (dx * dx + dy * dy) >= DRAG_THRESHOLD * DRAG_THRESHOLD) {
      s.moved = true;
      setFabDragging(true);
    }
    if (!s.moved) return;
    const root = fabRootRef.current;
    if (!root) return;
    const { width, height } = root.getBoundingClientRect();
    const next = clampFabPosition(
      e.clientX - s.offsetX,
      e.clientY - s.offsetY,
      width,
      height
    );
    setFabDragPos(next);
  };

  const fabPointerUp = (e) => {
    const s = fabDragState.current;
    if (s.pointerId !== e.pointerId) return;
    const wasMoved = s.moved;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_err) {
      /* ignore */
    }
    fabDragState.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      moved: false,
    };

    if (wasMoved) {
      const root = fabRootRef.current;
      if (root) {
        const r = root.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const corner = nearestCorner(cx, cy);
        setFabCorner(corner);
        persistCorner(FAB_CORNER_KEY, corner);
      }
      setFabDragPos(null);
      setFabDragging(false);
    } else {
      setOpen((v) => !v);
    }
  };

  const fabPointerCancel = (e) => {
    const s = fabDragState.current;
    if (s.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_err) {
      /* ignore */
    }
    fabDragState.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      moved: false,
    };
    setFabDragPos(null);
    setFabDragging(false);
  };

  const panelHeaderPointerDown = (e) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    panelDragState.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_err) {
      /* ignore */
    }
  };

  const panelHeaderPointerMove = (e) => {
    const s = panelDragState.current;
    if (s.pointerId !== e.pointerId) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && (dx * dx + dy * dy) >= DRAG_THRESHOLD * DRAG_THRESHOLD) {
      s.moved = true;
      setPanelDragging(true);
    }
    if (!s.moved) return;
    const panel = panelRef.current;
    if (!panel) return;
    const { width, height } = panel.getBoundingClientRect();
    const next = clampFabPosition(
      e.clientX - s.offsetX,
      e.clientY - s.offsetY,
      width,
      height
    );
    setPanelDragPos(next);
  };

  const panelHeaderPointerUp = (e) => {
    const s = panelDragState.current;
    if (s.pointerId !== e.pointerId) return;
    const wasMoved = s.moved;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_err) {
      /* ignore */
    }

    if (wasMoved) {
      const panel = panelRef.current;
      if (panel) {
        const r = panel.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const corner = nearestCorner(cx, cy);
        setPanelCorner(corner);
        persistCorner(PANEL_CORNER_KEY, corner);
      }
      setPanelDragPos(null);
      setPanelDragging(false);
    }
    panelDragState.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      moved: false,
    };
  };

  const panelHeaderPointerCancel = (e) => {
    const s = panelDragState.current;
    if (s.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_err) {
      /* ignore */
    }
    panelDragState.current = {
      pointerId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      moved: false,
    };
    setPanelDragPos(null);
    setPanelDragging(false);
  };

  const cornerClassFab = css[`corner${fabCorner.toUpperCase()}`];
  const cornerClassPanel = css[`cornerPanel${panelCorner.toUpperCase()}`];

  const fabRootClass = [
    css.fabRoot,
    !fabDragPos && cornerClassFab,
    fabCorner === "bl" || fabCorner === "tl" ? css.fabRootLeft : "",
    fabDragging ? css.fabRootDragging : "",
  ]
    .filter(Boolean)
    .join(" ");

  const fabRootStyle =
    fabDragPos != null
      ? {
          left: fabDragPos.left,
          top: fabDragPos.top,
          right: "auto",
          bottom: "auto",
        }
      : undefined;

  const panelTransformOrigin = {
    br: "bottom right",
    bl: "bottom left",
    tr: "top right",
    tl: "top left",
  }[panelCorner];

  const panelClass = [
    css.panel,
    !panelDragPos && cornerClassPanel,
    panelDragging ? css.panelDragging : "",
  ]
    .filter(Boolean)
    .join(" ");

  const panelStyle =
    panelDragPos != null
      ? {
          left: panelDragPos.left,
          top: panelDragPos.top,
          right: "auto",
          bottom: "auto",
          transformOrigin: panelTransformOrigin,
        }
      : { transformOrigin: panelTransformOrigin };

  const showFabShy =
    isShy && !prefersReducedMotion && !open && introStep === 0 && !fabHovered;

  return (
    <>
      <div
        ref={fabRootRef}
        className={fabRootClass}
        style={fabRootStyle}
        onMouseEnter={() => setFabHovered(true)}
        onMouseLeave={() => setFabHovered(false)}
        onFocusCapture={() => setFabHovered(true)}
        onBlurCapture={(ev) => {
          if (!ev.currentTarget.contains(ev.relatedTarget)) setFabHovered(false);
        }}
      >
        {introStep > 0 && (
          <div
            className={css.introStack}
            aria-live="polite"
            aria-label="Stella introduction"
          >
            <button
              type="button"
              className={css.introDismiss}
              aria-label="Dismiss introduction"
              onClick={dismissIntro}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            {introStep >= 1 && (
              <div className={`${css.introBubble} ${css.introBubbleFirst}`}>
                {STELLA_LINE1}
              </div>
            )}
            {introStep >= 2 && (
              <div className={`${css.introBubble} ${css.introBubbleSecond}`}>
                {STELLA_LINE2}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          className={`${css.fab} ${showFabShy ? css.fabShy : ""} ${fabDragging ? css.fabDragging : ""}`}
          aria-label={open ? "Close Platform Help" : "Open Platform Help"}
          aria-expanded={open}
          onPointerDown={fabPointerDown}
          onPointerMove={fabPointerMove}
          onPointerUp={fabPointerUp}
          onPointerCancel={fabPointerCancel}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
        >
          <FontAwesomeIcon icon={faCircleQuestion} />
        </button>
      </div>

      {open && (
        <div
          ref={panelRef}
          className={panelClass}
          style={panelStyle}
          role="dialog"
          aria-label="Platform Help chat"
          aria-modal="false"
        >
          <div
            className={`${css.header} ${panelDragging ? css.headerGrabbing : ""}`}
            onPointerDown={panelHeaderPointerDown}
            onPointerMove={panelHeaderPointerMove}
            onPointerUp={panelHeaderPointerUp}
            onPointerCancel={panelHeaderPointerCancel}
            role="presentation"
          >
            <span className={css.headerDragDots} aria-hidden="true" />
            <span className={css.title}>Platform Help</span>
            <button
              type="button"
              className={css.headerBtn}
              onPointerDown={(ev) => ev.stopPropagation()}
              onClick={(ev) => {
                ev.stopPropagation();
                handleNewChat();
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> New chat
            </button>
            <button
              type="button"
              className={css.headerBtn}
              aria-label="Close Platform Help"
              onPointerDown={(ev) => ev.stopPropagation()}
              onClick={(ev) => {
                ev.stopPropagation();
                setOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className={css.messages}>
            {messages.length === 0 && !loading && (
              <div className={css.welcome}>
                <h3>How can I help?</h3>
                <p>
                  Ask how to use the LMS: navigation, courses, classes,
                  sessions, admin tools, and troubleshooting.
                </p>
                <div className={css.suggestions}>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={css.suggestionBtn}
                      onClick={() => sendText(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`${css.message} ${css[msg.type] || ""}`}
              >
                <div
                  className={`${css.bubble} ${
                    msg.hasError ? css.errorBubble : ""
                  }`}
                >
                  {msg.type === "assistant" ? (
                    <ReactMarkdown>{msg.content || ""}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                  {msg.isProcessing && !msg.content && (
                    <div className={css.thinking}>Thinking…</div>
                  )}
                </div>
                {msg.type === "assistant" &&
                  Array.isArray(msg.links) &&
                  msg.links.length > 0 && (
                    <div className={css.linkChips} role="group" aria-label="Suggested pages">
                      {msg.links.map((link, i) => (
                        <button
                          key={`${link.href}-${i}`}
                          type="button"
                          className={css.linkChip}
                          aria-label={`Open ${link.label || link.href}`}
                          onClick={() => {
                            history.push(link.href);
                            setOpen(false);
                          }}
                        >
                          {link.label || link.href}
                        </button>
                      ))}
                    </div>
                  )}
                <div className={css.time}>{msg.timestamp}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className={css.inputRow}>
            <input
              className={css.input}
              placeholder="Ask about using the platform…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={loading}
            />
            <button
              type="button"
              className={css.sendBtn}
              disabled={!input.trim() || loading}
              onClick={() => sendText(input)}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
